import React, { useEffect, useState } from 'react';

import { Close as CloseIcon, ReadMore, Search as SearchIcon } from '@mui/icons-material';
import { Box, Button, Grid, IconButton, InputAdornment, TextField } from '@mui/material';
import { get, set } from 'lodash';
import { useNavigate, useLocation } from 'react-router-dom';

import { STATUS_REDACT_CUSTOMER_DATA } from 'constants/status';
import AxlAutocomplete from '../../components/AxlAutocomplete';
import AxlTableContainer from '../../components/AxlTableContainer';
import { getRedactCustomerData, createRedactCustomerData } from '../../repositories/HttpRepository';
import AddNew from './AddNew';
import { toast } from 'react-toastify';
import moment from 'moment';

export const FIELD_NAME = {
  CLIENT: 'client_id',
  STATUS: 'status',
  CREATED_BY: 'created_by',
  CREATED_AT: '_created',
  NOTE: 'note',
  TOTAL_SHIPMENTS: 'total_shipments',
  PROCESSED_SHIPMENTS: 'processed_shipments',
};

export const DEFAULT_FILTERS = {
  client_id: null,
  statuses: null,
  page: 1,
  size: 20,
};

function RedactCustomerInformation() {
  const navigate = useNavigate();
  const location = useLocation();
  const [tableData, setTableData] = useState({});
  const [loading, setLoading] = useState(false);
  const [firstLoad, setFirstLoad] = useState(true);
  const [openAddNew, setOpenAddNew] = useState(false);
  const [query, setQuery] = useState('');
  const queryParams = new URLSearchParams(location.search);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  const updateFilter = (name, value) => {
    setFilters({ ...filters, [name]: value });
  };

  const updateFilters = (updated) => {
    setFilters({ ...filters, ...updated });
  };

  useEffect(() => {
    // no-dd-sa:javascript-code-style/assignment-name
    const client_ids = queryParams.get('client_ids');
    const statuses = queryParams.get('statuses')?.split(',');
    const page = queryParams.get('page') ? parseInt(queryParams.get('page'), 10) : 1;
    const updatedFilters = { client_ids, statuses, page };
    setQuery(client_ids);
    setFilters({ ...filters, ...updatedFilters });
  }, []);

  useEffect(() => {
    if (firstLoad) {
      setFirstLoad(false);
      return;
    }

    setLoading(true);
    fetchList();
  }, [filters]);

  // get list of redact clients
  const fetchList = () => {
    const params = {
      ...filters,
      page: filters.page,
      statuses: Array.isArray(filters.statuses) ? filters.statuses?.join(',') : filters.statuses,
    };
    getRedactCustomerData(params).then((res) => {
      setLoading(false);
      if (res.ok) {
        setTableData(res.data);
      } else {
        setTableData({});
      }
      updateQuery();
    });
  };

  // create a new redaction of customer data
  const handleConfirm = (clientId, totalShipments, note) => {
    const params = {
      client_id: clientId,
      total_shipments: totalShipments,
      note,
    };

    createRedactCustomerData(params).then((res) => {
      setOpenAddNew(false);
      if (res.ok) {
        toast.success('Add new redaction of customer data successfully');
        fetchList();
        return;
      } else if (res.status === 403) {
        toast.error('You do not have permission to create a new customer data redaction request.');
        return;
      }

      toast.error('There was an error while adding new redaction of customer data.');
      return;
    });
  };

  const updateQuery = () => {
    let filterParams = Object.keys(filters)
      .filter((k) => !!filters[k])
      .reduce((a, k) => ({ ...a, [k]: filters[k] }), {});

    delete filterParams['desc'];
    delete filterParams['order_by'];
    delete filterParams['size'];

    navigate(
      {
        search: '?' + new URLSearchParams(filterParams).toString(),
      },
      { replace: true },
    );
  };

  const handleChange = (val, setField) => {
    setField(val);
  };

  const onPageChange = (page) => {
    if (page !== filters.page) {
      updateFilter('page', page);
    }
  };

  const handleChangeStatuses = (values) => {
    const status = values.map((c) => c.value);
    updateFilters({
      statuses: !status.length ? null : status.join(','),
      page: DEFAULT_FILTERS.page,
    });
  };

  const handleClear = () => {
    setQuery('');
    updateFilters({ client_ids: null, page: DEFAULT_FILTERS.page });
  };

  const handleCloseAddNew = () => {
    setOpenAddNew(false);
  };

  const handleKeyDown = (evt) => {
    if (evt.keyCode === 13) {
      const queryTrim = query ? query.trim() : null;
      setQuery(queryTrim);
      updateFilters({ client_ids: queryTrim, page: DEFAULT_FILTERS.page });
    }
  };

  const fields = [
    {
      fieldName: FIELD_NAME.CLIENT,
      label: 'Client',
      isDisableSort: true,
      renderer: (val, item) => (val ? `${item.client_name} [${item.client_id}]` : ''),
    },
    {
      fieldName: FIELD_NAME.PROCESSED_SHIPMENTS,
      label: 'Processed shipments',
      isDisableSort: true,
      renderer: (val) => val || 0,
    },
    {
      fieldName: FIELD_NAME.TOTAL_SHIPMENTS,
      label: 'Total shipments',
      isDisableSort: true,
      renderer: (val) => val || 0,
    },
    {
      fieldName: FIELD_NAME.NOTE,
      label: 'Note',
      isDisableSort: true,
      width: '300px',
    },
    {
      fieldName: FIELD_NAME.CREATED_BY,
      label: 'Created by',
      isDisableSort: true,
      renderer: (val, item) => (val ? `${item.created_by_str} [${item.created_by}]` : ''),
    },
    {
      fieldName: FIELD_NAME.CREATED_AT,
      label: 'Created at',
      isDisableSort: true,
      renderer: (val, _) => (val ? moment(val).format('YYYY-MM-DD HH:mm z') : ''),
    },
    {
      fieldName: FIELD_NAME.STATUS,
      label: 'Status',
      isDisableSort: true,
      renderer: (val) => val || '',
    },
  ];

  const selectedStatus = STATUS_REDACT_CUSTOMER_DATA?.filter((c) => filters?.statuses?.includes(c.value));

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={2} sx={{ alignItems: 'center' }}>
        <Grid size={1}>
          <Button style={{ padding: '8px 0px', width: '100%' }} variant="contained" color="primary" onClick={() => setOpenAddNew(true)} size="small">
            + NEW
          </Button>
        </Grid>
        <Grid size={3}>
          <AxlAutocomplete
            limitTags={1}
            isMultiple={true}
            options={STATUS_REDACT_CUSTOMER_DATA}
            label="Status"
            handleChange={(_, selected) => handleChangeStatuses(selected)}
            value={selectedStatus}
            getOptionLabel={(option) => option.label}
          />
        </Grid>
        <Grid size={8}>
          <TextField
            fullWidth
            onKeyDown={handleKeyDown}
            onChange={(evt) => handleChange(evt.target.value, setQuery)}
            variant="outlined"
            placeholder="Search by Client ID"
            size="small"
            value={query ?? ''}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: query && (
                  <IconButton onClick={() => handleClear()} size="small" disableElevation>
                    <CloseIcon />
                  </IconButton>
                ),
              },
            }}
          />
        </Grid>
      </Grid>
      <Box sx={{ mt: 3 }}>
        <AxlTableContainer
          fields={fields}
          result={tableData}
          filters={filters}
          isLoading={loading}
          tableStyles={{ maxHeight: 'none' }}
          onPageChange={onPageChange}
          pagination={get(tableData, 'total') > 0}
        />
      </Box>
      {openAddNew && <AddNew excludeClientIds={tableData?.client_ids} isOpen={openAddNew} onClose={handleCloseAddNew} onConfirm={handleConfirm} />}
    </Box>
  );
}

export default RedactCustomerInformation;
